import useTvApi, { messageOf } from "hooks/useTvApi";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, EmptyState, Icon, useToast } from "stories/Admin";
import { DailyPoster } from "types/TvPosterTypes";
import styles from "./DailyTvPoster.module.scss";

/*
 * Superadmin → Dagens hadith / Dagens vers: TV-plakaten som er laget av dagens
 * innhold (liggende og stående), og en knapp for å tegne den på nytt.
 * Plakaten lages også når innholdet genereres på nytt.
 */

interface Props {
  kind: DailyPoster["kind"];
  /** Endres når innholdet er generert på nytt, så plakaten hentes igjen. */
  refreshKey?: unknown;
}

const LABEL: Record<DailyPoster["kind"], string> = {
  QURAN: "dagens vers",
  HADITH: "dagens hadith",
};

const DailyTvPoster: React.FC<Props> = ({ kind, refreshKey }) => {
  const api = useTvApi();
  const toast = useToast();
  const [poster, setPoster] = useState<DailyPoster | null | undefined>(
    undefined,
  );
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () =>
      api<DailyPoster[]>("/api/v1/tv/daily-posters/today", "GET")
        .then((list) =>
          setPoster((list ?? []).find((p) => p.kind === kind) ?? null),
        )
        .catch(() => setPoster(null)),
    [api, kind],
  );

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const render = async () => {
    setBusy(true);
    try {
      const saved = await api<DailyPoster>(
        `/api/v1/tv/daily-posters/${kind}/render`,
        "POST",
      );
      setPoster(saved);
      toast.success(`TV-plakaten for ${LABEL[kind]} er tegnet på nytt.`);
    } catch (e) {
      toast.error(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="TV-plakat"
      subtitle={`Plakaten moskeene kan vise på TV, og som deles fra appen sammen med ${LABEL[kind]}.`}
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={render}
          loading={busy}
          icon={<Icon name="wand" size={16} strokeWidth={2} />}
        >
          Tegn plakaten på nytt
        </Button>
      }
    >
      {poster === undefined ? null : poster ? (
        <div className={styles.images}>
          <a
            href={poster.plakat}
            target="_blank"
            rel="noreferrer"
            className={styles.landscape}
          >
            <img src={poster.plakat} alt={`${poster.title}, liggende`} />
          </a>
          <a
            href={poster.plakatPortrait}
            target="_blank"
            rel="noreferrer"
            className={styles.portrait}
          >
            <img src={poster.plakatPortrait} alt={`${poster.title}, stående`} />
          </a>
        </div>
      ) : (
        <EmptyState icon="tv" title="Ingen plakat for i dag ennå">
          Den lages når {LABEL[kind]} er klar, eller når du trykker «Tegn
          plakaten på nytt».
        </EmptyState>
      )}
    </Card>
  );
};

export default DailyTvPoster;
